import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Difficulty } from "@hunch/core";
import { useI18n } from "../../i18n/index.js";
import styles from "./EndScreen.module.css";

export interface RoundSummary {
  points: number;
  maxPoints: number;
  /** Number of green/filled squares in this round's share-text emoji row. */
  greenSquares: number;
  /** Total squares in this round's share-text emoji row. */
  totalSquares: number;
}

export interface EndScreenProps {
  gameName: string;
  difficulty: Difficulty;
  totalScore: number;
  maxScore: number;
  rounds: RoundSummary[];
  onReplay: () => void;
}

export function EndScreen({ gameName, difficulty, totalScore, maxScore, rounds, onReplay }: EndScreenProps) {
  const { t } = useI18n();
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const fallbackRef = useRef<HTMLTextAreaElement>(null);

  const difficultyLabel = t(`timeslip.start.difficulty.${difficulty}`);
  const shareText = buildShareText(gameName, difficultyLabel, totalScore, maxScore, rounds);

  useEffect(() => {
    if (copyState === "failed" && fallbackRef.current) {
      fallbackRef.current.focus();
      fallbackRef.current.select();
    }
  }, [copyState]);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  }

  return (
    <main className={styles.main}>
      <p className={styles.score}>
        {totalScore} / {maxScore}
      </p>

      <ul className={styles.roundList}>
        {rounds.map((round, i) => (
          <li key={i} className={styles.roundRow}>
            <span>
              {t("endscreen.round")} {i + 1}
            </span>
            <span className={styles.squares}>{emojiRow(round)}</span>
            <span>{round.points} P.</span>
          </li>
        ))}
      </ul>

      <div className={styles.actions}>
        <button type="button" className={styles.secondaryButton} onClick={handleCopy}>
          {copyState === "copied" ? t("endscreen.copied") : t("endscreen.copyResult")}
        </button>
        <button type="button" className={styles.primaryButton} onClick={onReplay}>
          {t("endscreen.replay")}
        </button>
        <Link to="/" className={styles.secondaryButton}>
          {t("common.backHome")}
        </Link>
      </div>

      {copyState === "failed" && (
        <textarea
          ref={fallbackRef}
          className={styles.fallbackText}
          readOnly
          value={shareText}
          aria-label={t("endscreen.copyResult")}
        />
      )}
    </main>
  );
}

function emojiRow(round: RoundSummary): string {
  const filled = "🟨".repeat(Math.max(0, Math.min(round.greenSquares, round.totalSquares)));
  const rest = "⬜".repeat(Math.max(0, round.totalSquares - round.greenSquares));
  return filled + rest;
}

function buildShareText(
  gameName: string,
  difficultyLabel: string,
  totalScore: number,
  maxScore: number,
  rounds: RoundSummary[],
): string {
  const lines = [
    `${gameName} (${difficultyLabel}): ${totalScore}/${maxScore}`,
    ...rounds.map(emojiRow),
  ];
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  if (origin) lines.push(origin);
  return lines.join("\n");
}
